import { motion } from 'framer-motion'
import {
  Zap,
  Upload,
  BarChart2,
  AlertTriangle,
  CheckCircle,
  Brain,
  Target,
  Mail,
} from 'lucide-react'

const fadeUp = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
}

export function HelpPage() {
  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      <div className="mb-8">
        <h1 className="page-title">How AI CV Screening Works</h1>
        <p className="page-subtitle">
          Understanding the ranking process helps you get the most accurate
          results
        </p>
      </div>

      <div className="space-y-5">
        <motion.div {...fadeUp} className="card p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl bg-brand-50 flex items-center justify-center">
              <Mail className="w-5 h-5 text-brand-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">
              Support contact
            </h2>
          </div>
          <p className="text-gray-600 text-sm">
            For support in French or English, contact{' '}
            <a
              href="mailto:hello@getworkflowpro.com"
              className="font-semibold text-brand-600 hover:text-brand-700"
            >
              hello@getworkflowpro.com
            </a>
            .
          </p>
        </motion.div>

        {/* The process */}
        <motion.div {...fadeUp} className="card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-xl bg-brand-50 flex items-center justify-center">
              <Zap className="w-5 h-5 text-brand-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">
              The ranking process
            </h2>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            When you upload CVs the tool follows four steps automatically:
          </p>
          <div className="space-y-3">
            {[
              {
                step: '1',
                icon: Upload,
                title: 'Text Extraction',
                desc: 'Amazon Textract reads the CV and extracts all text from PDF or DOCX files accurately.',
                color: 'bg-blue-100 text-blue-700',
              },
              {
                step: '2',
                icon: Brain,
                title: 'AI Parsing',
                desc: 'The AI turns the raw CV text into a structured candidate profile with skills, work history, education, certifications and other relevant context.',
                color: 'bg-purple-100 text-purple-700',
              },
              {
                step: '3',
                icon: Target,
                title: 'AI-First Evaluation',
                desc: 'The AI evaluates the candidate against the full job description, including domain fit, must-have requirements, scope, seniority, tools and major gaps.',
                color: 'bg-green-100 text-green-700',
              },
              {
                step: '4',
                icon: BarChart2,
                title: 'Ranking',
                desc: 'Candidates are ranked by overall fit so the strongest matches appear first, with an explanation you can review alongside the score.',
                color: 'bg-orange-100 text-orange-700',
              },
            ].map((item) => {
              const Icon = item.icon
              return (
                <div key={item.step} className="flex gap-4 items-start">
                  <div
                    className={`w-8 h-8 rounded-xl ${item.color} flex items-center justify-center shrink-0 font-bold text-sm`}
                  >
                    {item.step}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">
                      {item.title}
                    </p>
                    <p className="text-gray-500 text-sm mt-0.5">{item.desc}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </motion.div>

        {/* Score breakdown */}
        <motion.div
          {...fadeUp}
          transition={{ delay: 0.1 }}
          className="card p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-xl bg-green-50 flex items-center justify-center">
              <BarChart2 className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">
              How scores are calculated
            </h2>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            The main score is an AI-first fit score out of 100. It is not a fixed
            rule formula. The AI weighs several hiring signals together:
          </p>
          <div className="space-y-3">
            {[
              {
                label: 'Core Domain Fit',
                tag: 'Priority signal',
                color: 'bg-blue-500',
                bgColor: 'bg-blue-50',
                textColor: 'text-blue-900',
                desc: 'How closely the candidate matches the actual domain and type of role you are hiring for.',
              },
              {
                label: 'Must-Have Requirements',
                tag: 'Priority signal',
                color: 'bg-green-500',
                bgColor: 'bg-green-50',
                textColor: 'text-green-900',
                desc: 'Whether the candidate appears to meet the non-negotiable requirements in the job description.',
              },
              {
                label: 'Seniority and Experience Fit',
                tag: 'Fit signal',
                color: 'bg-purple-500',
                bgColor: 'bg-purple-50',
                textColor: 'text-purple-900',
                desc: 'Whether the level of experience and likely seniority match the role, not just total years on paper.',
              },
              {
                label: 'Responsibility and Scope',
                tag: 'Fit signal',
                color: 'bg-orange-500',
                bgColor: 'bg-orange-50',
                textColor: 'text-orange-900',
                desc: 'Whether the candidate has handled similar ownership, scale, complexity or leadership expectations.',
              },
              {
                label: 'Tools and Platform Fit',
                tag: 'Fit signal',
                color: 'bg-cyan-500',
                bgColor: 'bg-cyan-50',
                textColor: 'text-cyan-900',
                desc: 'Whether the candidate has relevant tools, platforms, frameworks or environment experience.',
              },
              {
                label: 'Red Flags and Major Gaps',
                tag: 'Risk signal',
                color: 'bg-rose-500',
                bgColor: 'bg-rose-50',
                textColor: 'text-rose-900',
                desc: 'Clear gaps, missing must-haves or risk factors can reduce the score even if some experience looks strong.',
              },
              {
                label: 'Recruiter-Style Explanation',
                tag: 'Review signal',
                color: 'bg-slate-500',
                bgColor: 'bg-slate-50',
                textColor: 'text-slate-900',
                desc: 'The AI writes a concise explanation of why the candidate ranked where they did so you can review the reasoning quickly.',
              },
            ].map((item) => (
              <div
                key={item.label}
                className={`flex items-center justify-between gap-4 p-3 rounded-xl ${item.bgColor}`}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <p className={`text-sm font-semibold ${item.textColor}`}>
                      {item.label}
                    </p>
                    <span className={`text-[11px] font-semibold uppercase tracking-wide ${item.textColor} opacity-70`}>
                      {item.tag}
                    </span>
                  </div>
                  <p className={`text-xs mt-0.5 ${item.textColor} opacity-70`}>
                    {item.desc}
                  </p>
                </div>
                <div className={`w-3 h-3 rounded-full ${item.color} shrink-0`} />
              </div>
            ))}
          </div>
          <div className="bg-gray-50 rounded-xl p-4 mt-4">
            <p className="text-sm text-gray-600">
              A rule scorer still exists as a fallback and sanity check, but it is
              not the main ranking system. The primary ranking is AI-first.
            </p>
          </div>
        </motion.div>

        {/* AI-first matching */}
        <motion.div
          {...fadeUp}
          transition={{ delay: 0.2 }}
          className="card p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-xl bg-yellow-50 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">
              AI-first matching is broader than keyword matching
            </h2>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            The current system can understand equivalent wording better than an
            exact keyword-only approach. A strong candidate does not need to use
            the exact same phrasing as the job description to rank well.
          </p>
          <div className="space-y-3">
            {[
              {
                jd: 'Machine learning for forecasting',
                cv: 'Predictive modelling and demand planning',
              },
              {
                jd: 'People management in a scaling team',
                cv: 'Led and mentored a growing delivery team',
              },
            ].map((example, i) => (
              <div
                key={i}
                className="bg-yellow-50 border border-yellow-200 rounded-xl p-4"
              >
                <p className="text-xs font-bold text-yellow-800 mb-2">
                  EXAMPLE
                </p>
                <div className="flex items-center gap-3 text-sm">
                  <div className="flex-1">
                    <p className="text-xs text-yellow-700 mb-1">
                      Job description says
                    </p>
                    <span className="font-semibold text-yellow-900">
                      {example.jd}
                    </span>
                  </div>
                  <div className="text-yellow-400 font-bold">vs</div>
                  <div className="flex-1">
                    <p className="text-xs text-yellow-700 mb-1">
                      Candidate writes
                    </p>
                    <span className="font-semibold text-yellow-900">
                      {example.cv}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-yellow-700 mt-2">
                  Similar meaning, different wording. The AI can often recognise
                  this relationship even when the terms are not identical.
                </p>
              </div>
            ))}
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mt-4">
            <p className="text-sm text-yellow-900">
              This does not mean every strong candidate will always score
              perfectly. Ambiguous CVs, weak evidence or missing must-haves can
              still push a candidate down the ranking, so manual review remains
              important.
            </p>
          </div>
        </motion.div>

        {/* Best practices */}
        <motion.div
          {...fadeUp}
          transition={{ delay: 0.3 }}
          className="card p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-xl bg-green-50 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">
              How to get the most accurate rankings
            </h2>
          </div>
          <div className="space-y-3">
            {[
              {
                title: 'Write detailed job descriptions',
                desc: 'The AI performs best when the role description clearly explains the domain, goals, scope and must-have requirements.',
              },
              {
                title: 'Be explicit about non-negotiables',
                desc: 'Call out hard requirements clearly so the AI can separate nice-to-haves from true blockers.',
              },
              {
                title: 'Describe seniority and scope',
                desc: 'Include whether you need execution, ownership, leadership, stakeholder management or experience at a particular scale.',
              },
              {
                title: 'Use the candidate breakdown',
                desc: 'Click View on any candidate to review the AI score, recommendation, explanation, matched evidence, missing evidence and CV details.',
              },
              {
                title: 'Review edge cases manually',
                desc: 'Use the ranking to focus your attention, then manually review promising borderline candidates and obvious outliers.',
              },
              {
                title: 'Keep job and CV language as aligned as practical',
                desc: 'AI can handle equivalent wording better than exact keyword matching, but clearer source material still improves consistency.',
              },
            ].map((item, i) => (
              <div key={i} className="flex gap-3 items-start">
                <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {item.title}
                  </p>
                  <p className="text-sm text-gray-500 mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* What AI can and cannot do */}
        <motion.div
          {...fadeUp}
          transition={{ delay: 0.4 }}
          className="card p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center">
              <Brain className="w-5 h-5 text-purple-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">
              What the AI can and cannot do
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-semibold text-green-700 mb-3 flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4" />
                The AI is good at
              </p>
              <ul className="space-y-2">
                {[
                  'Processing large CV volumes quickly',
                  'Understanding equivalent wording better than exact keyword matching',
                  'Comparing candidates against the full job description',
                  'Highlighting strengths, gaps and red flags',
                  'Writing concise recruiter-style explanations',
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2 text-sm text-gray-600"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 mt-1.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-sm font-semibold text-red-600 mb-3 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4" />
                The AI cannot
              </p>
              <ul className="space-y-2">
                {[
                  'Replace your professional judgement',
                  'Make an automatic hiring decision',
                  'Guarantee that every important nuance is captured',
                  'Assess culture fit, motivation or interview performance',
                  'Confirm claims that are weakly evidenced in the CV',
                  'Remove the need for human review',
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2 text-sm text-gray-600"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 mt-4">
            <p className="text-sm text-gray-600 text-center">
              The score is <strong>decision support</strong>, not an automatic
              hiring decision. Use it to focus your attention, then apply manual
              review and professional judgement before you shortlist or reject
              anyone.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
